import React, { useEffect, useRef } from 'react';
import * as LightweightCharts from 'lightweight-charts';
import './TokenChart.css';

const TokenChart = ({ token, height = 400, timeframe = '5m', metric = 'price' }) => {
  const chartContainerRef = useRef();
  const chartRef = useRef();

  useEffect(() => {
    if (!chartContainerRef.current) return;

    try {
      // Create chart with visible price scale
      const chart = LightweightCharts.createChart(chartContainerRef.current, {
        width: chartContainerRef.current.clientWidth,
        height: height,
        layout: {
          background: { color: '#0f1118' },
          textColor: '#d1d4dc',
        },
        grid: {
          vertLines: { color: '#1a1d2a' },
          horzLines: { color: '#1a1d2a' },
        },
        crosshair: {
          mode: LightweightCharts.CrosshairMode.Normal,
          vertLine: {
            width: 1,
            color: '#758696',
            style: 0,
          },
          horzLine: {
            width: 1,
            color: '#758696',
            style: 0,
          },
        },
        rightPriceScale: {
          borderColor: '#2a2d3a',
          visible: true,
          scaleMargins: {
            top: 0.1,
            bottom: 0.1,
          },
        },
        timeScale: {
          borderColor: '#2a2d3a',
          timeVisible: true,
          secondsVisible: false,
          rightOffset: 5,
          barSpacing: 10,
        },
      });

      chartRef.current = chart;

      // Add candlestick series
      const candlestickSeries = chart.addCandlestickSeries({
        upColor: '#22c55e',
        downColor: '#ef4444',
        borderUpColor: '#22c55e',
        borderDownColor: '#ef4444',
        wickUpColor: '#22c55e',
        wickDownColor: '#ef4444',
        priceFormat: {
          type: 'price',
          precision: 8,
          minMove: 0.00000001,
        },
      });

      // Generate dummy data based on timeframe and metric
      const generateDummyData = () => {
        const usdPrice = token.usdPrice || 0.0001;
        const mcap = token.mcap || token.marketCap || 0;

        // Derive a base value for chart depending on metric
        let baseValue;
        if (metric === 'marketCap') {
          // Keep numbers reasonable by scaling MC down
          const scaledMcap = mcap > 0 ? mcap / 1e6 : usdPrice * 1e6;
          baseValue = scaledMcap || 1;
        } else {
          baseValue = usdPrice || 0.0001;
        }

        const data = [];
        const now = Math.floor(Date.now() / 1000);
        
        // Map timeframe to seconds and number of candles
        const timeframeMap = {
          '1m': { interval: 60, candles: 100 },
          '5m': { interval: 300, candles: 100 },
          '10m': { interval: 600, candles: 100 },
          '30m': { interval: 1800, candles: 100 },
          '1h': { interval: 3600, candles: 100 },
          '6h': { interval: 21600, candles: 100 },
          '24h': { interval: 86400, candles: 100 }
        };

        const { interval, candles } = timeframeMap[timeframe] || timeframeMap['5m'];
        
        for (let i = candles; i > 0; i--) {
          const time = now - (i * interval);
          const volatility = 0.05; // 5% volatility
          const open = baseValue * (1 + (Math.random() - 0.5) * volatility);
          const close = baseValue * (1 + (Math.random() - 0.5) * volatility);
          const high = Math.max(open, close) * (1 + Math.random() * (volatility / 2));
          const low = Math.min(open, close) * (1 - Math.random() * (volatility / 2));

          data.push({
            time: time,
            open: open,
            high: high,
            low: low,
            close: close,
          });
        }
        return data;
      };

      const chartData = generateDummyData();
      candlestickSeries.setData(chartData);

      // Fit content to show all data
      chart.timeScale().fitContent();

      // Handle resize
      const handleResize = () => {
        if (chartContainerRef.current && chartRef.current) {
          chartRef.current.applyOptions({ 
            width: chartContainerRef.current.clientWidth 
          });
        }
      };

      window.addEventListener('resize', handleResize);

      // Cleanup
      return () => {
        window.removeEventListener('resize', handleResize);
        if (chartRef.current) {
          chartRef.current.remove();
          chartRef.current = null;
        }
      };
    } catch (error) {
      console.error('Error initializing chart:', error);
    }
  }, [token, height, timeframe, metric]);

  // Header label + value depending on metric
  const headerPair = metric === 'marketCap'
    ? {
        label: `${token.symbol} / MC`,
        value: token.mcap || token.marketCap || 0,
        formatted: (() => {
          const v = token.mcap || token.marketCap || 0;
          if (!v) return '$0';
          if (v >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
          if (v >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
          if (v >= 1e3) return `$${(v / 1e3).toFixed(2)}K`;
          return `$${v.toFixed(2)}`;
        })(),
      }
    : {
        label: `${token.symbol} / USD`,
        value: token.usdPrice || 0,
        formatted: `$${(token.usdPrice || 0).toFixed(8)}`,
      };

  return (
    <div className="token-chart-container">
      <div className="chart-header">
        <h4>{headerPair.label}</h4>
        <div className="chart-price">
          {headerPair.formatted}
        </div>
      </div>
      <div ref={chartContainerRef} className="chart-wrapper" style={{ height: `${height}px` }} />
    </div>
  );
};

export default TokenChart;
